import React from 'react';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Badge,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Refresh as ResetIcon,
  Edit as EditIcon,
  FileDownload as DownloadIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  MoreVert as MoreIcon,
  FilterList as FilterIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { SAP_COLORS, SAP_SPACING, SAP_SHADOWS, SAP_TRANSITIONS } from '../../theme/sapDesignSystem';

// Icon type mapping
export type IconType = 
  | 'search' 
  | 'reset' 
  | 'edit' 
  | 'download' 
  | 'play' 
  | 'delete'
  | 'add'
  | 'save'
  | 'cancel'
  | 'view'
  | 'more'
  | 'filter'
  | 'settings'
  | 'info'
  | 'warning'
  | 'success'
  | 'error';

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'ghost';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface SAPIconButtonProps {
  icon: IconType;
  onClick: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  tooltip?: string;
  badge?: number | string;
  badgeColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  ariaLabel?: string;
  className?: string;
  sx?: any;
}

// Icon mapping
const iconMap = {
  search: SearchIcon,
  reset: ResetIcon,
  edit: EditIcon,
  download: DownloadIcon,
  play: PlayIcon,
  delete: DeleteIcon,
  add: AddIcon,
  save: SaveIcon,
  cancel: CancelIcon,
  view: ViewIcon,
  more: MoreIcon,
  filter: FilterIcon,
  settings: SettingsIcon,
  info: InfoIcon,
  warning: WarningIcon,
  success: SuccessIcon,
  error: ErrorIcon,
};

// Default tooltips for common actions
const defaultTooltips: Record<IconType, string> = {
  search: 'Search',
  reset: 'Reset filters',
  edit: 'Edit',
  download: 'Download',
  play: 'Run',
  delete: 'Delete',
  add: 'Add new',
  save: 'Save',
  cancel: 'Cancel',
  view: 'View details',
  more: 'More options',
  filter: 'Filter',
  settings: 'Settings',
  info: 'Information',
  warning: 'Warning',
  success: 'Success',
  error: 'Error',
};

// Styled IconButton following SAP design principles
const StyledIconButton = styled(IconButton)<{
  variant?: ButtonVariant;
  size?: ButtonSize;
}>(({ theme, variant = 'secondary', size = 'medium', disabled }) => {
  // Size configurations
  const sizeConfig = {
    small: {
      width: '32px',
      height: '32px',
      iconSize: '16px',
    },
    medium: {
      width: '40px',
      height: '40px',
      iconSize: '20px',
    },
    large: {
      width: '48px',
      height: '48px',
      iconSize: '24px',
    },
  };

  // Variant configurations
  const variantConfig = {
    primary: {
      backgroundColor: SAP_COLORS.sapBlue,
      color: SAP_COLORS.sapWhite,
      hoverBackground: SAP_COLORS.sapDarkBlue,
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
    },
    secondary: {
      backgroundColor: 'transparent',
      color: SAP_COLORS.sapGray6,
      hoverBackground: SAP_COLORS.hover,
      hoverColor: SAP_COLORS.sapBlue,
      border: `1px solid ${SAP_COLORS.sapGray3}`,
    },
    success: {
      backgroundColor: SAP_COLORS.sapGreen,
      color: SAP_COLORS.sapWhite,
      hoverBackground: '#2E7D32',
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
    },
    warning: {
      backgroundColor: SAP_COLORS.sapOrange,
      color: SAP_COLORS.sapWhite,
      hoverBackground: '#D84315',
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
    },
    error: {
      backgroundColor: SAP_COLORS.sapRed,
      color: SAP_COLORS.sapWhite,
      hoverBackground: '#C62828',
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
    },
    ghost: {
      backgroundColor: 'transparent',
      color: SAP_COLORS.sapGray5,
      hoverBackground: SAP_COLORS.hover,
      hoverColor: SAP_COLORS.sapGray7,
      border: 'none',
    },
  };

  const currentSize = sizeConfig[size];
  const currentVariant = variantConfig[variant];

  return {
    width: currentSize.width,
    height: currentSize.height,
    minWidth: currentSize.width,
    minHeight: currentSize.height,
    backgroundColor: currentVariant.backgroundColor,
    color: currentVariant.color,
    border: currentVariant.border,
    borderRadius: SAP_SPACING.component.borderRadius,
    transition: SAP_TRANSITIONS.fast,
    boxShadow: variant === 'primary' || variant === 'success' || variant === 'warning' || variant === 'error' 
      ? SAP_SHADOWS.sm 
      : 'none',

    '& .MuiSvgIcon-root': {
      fontSize: currentSize.iconSize,
      transition: SAP_TRANSITIONS.fast,
    },

    '&:hover': {
      backgroundColor: disabled ? currentVariant.backgroundColor : currentVariant.hoverBackground,
      color: disabled ? currentVariant.color : currentVariant.hoverColor,
      borderColor: variant === 'secondary' ? SAP_COLORS.sapBlue : currentVariant.border,
      boxShadow: disabled ? 'none' : (
        variant === 'primary' || variant === 'success' || variant === 'warning' || variant === 'error'
          ? SAP_SHADOWS.md 
          : SAP_SHADOWS.sm
      ),
      transform: disabled ? 'none' : 'translateY(-1px)',
    },

    '&:active': {
      transform: disabled ? 'none' : 'translateY(0)',
      boxShadow: disabled ? 'none' : SAP_SHADOWS.sm,
    },

    '&:focus-visible': {
      outline: `2px solid ${SAP_COLORS.sapBlue}`,
      outlineOffset: '2px',
    },

    '&.Mui-disabled': {
      backgroundColor: SAP_COLORS.disabled,
      color: SAP_COLORS.sapGray4,
      borderColor: SAP_COLORS.sapGray3,
      boxShadow: 'none',
      cursor: 'not-allowed',
    },
  };
});

const LoadingOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  borderRadius: SAP_SPACING.component.borderRadius,
});

export const SAPIconButton: React.FC<SAPIconButtonProps> = ({
  icon,
  onClick,
  variant = 'secondary',
  size = 'medium',
  disabled = false,
  loading = false,
  tooltip,
  badge,
  badgeColor = 'error',
  ariaLabel,
  className,
  sx,
}) => {
  const IconComponent = iconMap[icon];
  const finalTooltip = tooltip || defaultTooltips[icon];
  const finalAriaLabel = ariaLabel || finalTooltip;

  const iconButton = (
    <StyledIconButton
      onClick={onClick}
      variant={variant}
      size={size}
      disabled={disabled || loading}
      aria-label={finalAriaLabel}
      className={className}
      sx={{
        position: 'relative',
        ...sx,
      }}
    >
      <IconComponent />
      {loading && (
        <LoadingOverlay>
          <CircularProgress
            size={size === 'small' ? 12 : size === 'medium' ? 16 : 20}
            thickness={4}
            sx={{
              color: variant === 'primary' || variant === 'success' || variant === 'warning' || variant === 'error'
                ? SAP_COLORS.sapWhite
                : SAP_COLORS.sapBlue,
            }}
          />
        </LoadingOverlay>
      )}
    </StyledIconButton>
  );

  const buttonWithBadge = badge ? (
    <Badge
      badgeContent={badge}
      color={badgeColor}
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: badgeColor === 'error' ? SAP_COLORS.sapRed : 
                          badgeColor === 'warning' ? SAP_COLORS.sapOrange :
                          badgeColor === 'success' ? SAP_COLORS.sapGreen :
                          badgeColor === 'info' ? SAP_COLORS.sapBlue :
                          badgeColor === 'primary' ? SAP_COLORS.sapBlue :
                          SAP_COLORS.sapGray5,
          color: SAP_COLORS.sapWhite,
          fontSize: '0.625rem',
          fontWeight: 600,
          minWidth: '18px',
          height: '18px',
        },
      }}
    >
      {iconButton}
    </Badge>
  ) : iconButton;

  return (
    <Tooltip
      title={finalTooltip}
      arrow
      placement="top"
      sx={{
        '& .MuiTooltip-tooltip': {
          backgroundColor: SAP_COLORS.sapGray7,
          color: SAP_COLORS.sapWhite,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: `${SAP_SPACING.xs} ${SAP_SPACING.sm}`,
          borderRadius: SAP_SPACING.component.borderRadius,
        },
        '& .MuiTooltip-arrow': {
          color: SAP_COLORS.sapGray7,
        },
      }}
    >
      <span>
        {buttonWithBadge}
      </span>
    </Tooltip>
  );
};

export default SAPIconButton;